// Volumes in, thresholds out.
//
// The decision a person actually makes is how many leads Sales treats as
// qualified. The conversion rate at the boundary is the consequence of that
// decision, not the input to it, so the handle belongs on the volume and the
// rate belongs in the readout. The old form asked for three conversion rates and
// left the volumes to be discovered by saving.
//
// Everything here is derived from the leaf grid, which is the joint membership of
// leads across trees. The score of a combination is the weighted sum of one leaf
// rate per tree, so a single fetch supports every weighting: the ladder is
// recomputed in the browser as a weight divider moves, and no round trip is
// needed until the result is saved.

import { EnsembleThreshold } from '../../../models/ensembling';
import { Threshold } from '../../../models/ModelPerformances';
import {
  segmentColor,
  segmentTextColor,
  SEGMENT_LABEL,
} from '../trees/nodeSegment';
import { TIERS } from '../trees/treePerformance';

export interface LeafGridRow {
  nodes: number[];
  /** Leaf conversion rate as a percentage, one per tree, in `trees` order. */
  rates: number[];
  population: number;
  conversions: number;
}

export interface LeafGrid {
  /** Tree ids, the order every row's `rates` follows. */
  trees: number[];
  rows: LeafGridRow[];
  population: number;
  conversions: number;
}

/** One point total that occurs, and how many leads reach exactly it. */
export interface PointsGridRow {
  score: number;
  population: number;
  conversions: number;
}

export interface PointsGrid {
  rows: PointsGridRow[];
  /** Leads labelling puts in Low whatever their total: no email and no domain. */
  unscored?: { population: number; conversions: number };
}

export interface LadderRung {
  /** A blended score that actually occurs. */
  score: number;
  /** Leads scoring exactly this. */
  population: number;
  conversions: number;
  /** Leads scoring this or better. */
  cumulative: number;
  /** `cumulative` as a fraction of the population, 0 to 1. */
  share: number;
}

export interface TierOutcome {
  segment: Threshold;
  /** The segment name as the rest of the product writes it. */
  label: string;
  color: string | null;
  /** Text drawn ON `color`. Never white by default: see nodeSegment's ON_FILL. */
  textColor: string | null;
  /** The score at or above which a lead lands here. Null for low, which has no
   * threshold of its own: it is whatever clears none of the others. */
  threshold: number | null;
  population: number;
  conversions: number;
  /** Share of all leads, 0 to 1. */
  share: number;
  /** Share of all conversions, 0 to 1. */
  conversionShare: number;
  /** Conversion rate within the tier, as a percentage. */
  rate: number;
}

/**
 * Best first. Re-exported from the tree page's module rather than redeclared,
 * because the order carries meaning and two copies of it can disagree.
 */
export const TIER_ORDER: Threshold[] = TIERS;

/** The three tiers that have a threshold. Low is whatever clears none of them. */
export const BOUNDED_TIERS: Exclude<Threshold, 'low'>[] = [
  'very good',
  'good',
  'medium',
];

/**
 * Floor, never round.
 *
 * These values are handed back as thresholds and the test that uses them is
 * `score >= threshold`. A score rounded UP sits above every lead that produced
 * it, so the whole group falls into the tier below and the tier the boundary was
 * meant to open is silently empty. Flooring guarantees a threshold is reachable
 * by its own members. The prototype emptied a tier exactly this way, and the
 * page looked fine while it happened.
 */
export const floorScore = (value: number) => Math.floor(value * 1e6) / 1e6;

/** The blended score of one leaf combination at the given weights. */
export function blendScore(
  grid: LeafGrid,
  row: LeafGridRow,
  weights: Record<string, number>
): number {
  return grid.trees.reduce(
    (sum, treeId, index) =>
      sum + (Number(row.rates[index]) || 0) * (Number(weights[treeId]) || 0),
    0
  );
}

/**
 * Every blended score that occurs, best first, with the running share of the
 * population. A volume handle can only land on one of these: there is no lead
 * between two adjacent rungs, so a boundary drawn between them describes the same
 * split as the rung above it while claiming a volume nothing can deliver.
 */
export function buildLadder(
  grid: LeafGrid,
  weights: Record<string, number>
): LadderRung[] {
  if (!grid?.rows?.length) return [];

  return ladderOf(
    grid.rows.map((row) => ({
      score: floorScore(blendScore(grid, row, weights)),
      population: row.population,
      conversions: row.conversions,
    }))
  );
}

/**
 * A point based model's ladder. Its score IS the point total, so the rungs are
 * the totals that occur, with no blending and no weights: a point based model's
 * stored weights are whatever it last held as a tree model and mean nothing.
 */
export function pointsLadder(grid: PointsGrid | null): LadderRung[] {
  if (!grid?.rows?.length) return [];
  // The unscored leads are the bottom rung at -Infinity, below every threshold,
  // so they count in the shares and always tier as Low, as labelling puts them.
  const unscored = grid.unscored?.population
    ? [{ score: -Infinity, ...grid.unscored }]
    : [];
  return ladderOf([...grid.rows, ...unscored]);
}

/** Rows that each carry a score, grouped into rungs, best first. */
function ladderOf(rows: PointsGridRow[]): LadderRung[] {
  const groups = new Map<number, { population: number; conversions: number }>();
  rows.forEach((row) => {
    const group = groups.get(row.score) ?? { population: 0, conversions: 0 };
    group.population += Number(row.population) || 0;
    group.conversions += Number(row.conversions) || 0;
    groups.set(row.score, group);
  });

  const total = [...groups.values()].reduce(
    (sum, group) => sum + group.population,
    0
  );
  if (total <= 0) return [];

  let cumulative = 0;
  return [...groups.keys()]
    .sort((first, second) => second - first)
    .map((score) => {
      const group = groups.get(score);
      cumulative += group.population;
      return {
        score,
        population: group.population,
        conversions: group.conversions,
        cumulative,
        share: cumulative / total,
      };
    });
}

/**
 * Thresholds for the requested cumulative population shares.
 *
 * Each bound snaps to the rung whose share is closest to it, and the search for
 * the next bound starts one rung further down. Two bounds can therefore never
 * land on the same rung, which would collapse a tier to nothing while the
 * control still showed three separate handles.
 *
 * `bounds` are cumulative and best first: [0.12, 0.30, 0.60] asks for the top
 * 12% as Very Good, the next 18% as Good, the next 30% as Medium.
 */
export function thresholdsFromBounds(
  bounds: number[],
  ladder: LadderRung[]
): EnsembleThreshold | null {
  // A boundary sits on a score a threshold can take. The -Infinity rung of a
  // point based ladder holds leads no threshold reaches, and would serialise as
  // a null threshold.
  const finite = ladder.filter((rung) => Number.isFinite(rung.score));
  if (finite.length < BOUNDED_TIERS.length) return null;
  return pickThresholds(bounds, finite);
}

function pickThresholds(
  bounds: number[],
  ladder: LadderRung[]
): EnsembleThreshold {
  const wanted = bounds.slice(0, BOUNDED_TIERS.length);
  const indices: number[] = [];
  let from = 0;
  wanted.forEach((bound, position) => {
    // Every later bound still needs a rung of its own below this one, so the
    // window is bounded at both ends. Without the upper bound a pair of bounds
    // near 1 both snap to the last rung, two thresholds come out equal, and the
    // lower of the two tiers is silently empty while the control still draws its
    // handle. Clamping the search instead of the result keeps every tier
    // reachable whenever the ladder has a rung to spare per tier.
    const below = wanted.length - 1 - position;
    const limit = ladder.length - 1 - below;
    let best = Math.min(from, limit);
    let distance = Infinity;
    for (let index = best; index <= limit; index += 1) {
      const gap = Math.abs(ladder[index].share - bound);
      if (gap < distance) {
        distance = gap;
        best = index;
      }
    }
    indices.push(best);
    from = best + 1;
  });

  return BOUNDED_TIERS.reduce((out, segment, position) => {
    out[segment] = ladder[indices[position]].score;
    return out;
  }, {} as EnsembleThreshold);
}

/**
 * The cumulative shares a stored threshold set already implies, so the control
 * opens on what the model actually does rather than on a default.
 *
 * A tier whose threshold no lead reaches comes back at the share of the tier
 * above it, which is the honest reading: the tier is empty.
 */
export function boundsFromThresholds(
  thresholds: EnsembleThreshold,
  ladder: LadderRung[]
): number[] {
  return BOUNDED_TIERS.map((segment) => {
    const threshold = Number(thresholds?.[segment]);
    if (!Number.isFinite(threshold)) return 0;
    return ladder.reduce(
      (share, rung) => (rung.score >= threshold ? rung.share : share),
      0
    );
  });
}

/**
 * What a threshold set actually produces, per tier.
 *
 * Deliberately computed from the thresholds rather than from the requested
 * bounds. The two differ whenever a bound had to snap to a rung, and showing the
 * request back as though it were the outcome is how a control ends up lying: the
 * prototype wrote achieved shares back over the request on every redraw, which
 * made repeated weight changes drift the tiers away from what was asked for.
 * Requested bounds are the input and stay untouched; these numbers are the
 * consequence.
 */
export function tiersFromThresholds(
  thresholds: EnsembleThreshold,
  ladder: LadderRung[]
): TierOutcome[] {
  const total = ladder.length ? ladder[ladder.length - 1].cumulative : 0;
  const converted = ladder.reduce((sum, rung) => sum + rung.conversions, 0);
  const cuts = BOUNDED_TIERS.map(
    (segment) => Number(thresholds?.[segment]) || 0
  );

  const tierOf = (score: number): Threshold => {
    const hit = cuts.findIndex((cut) => score >= cut);
    return hit === -1 ? 'low' : BOUNDED_TIERS[hit];
  };

  const totals = TIER_ORDER.reduce((out, segment) => {
    out[segment] = { population: 0, conversions: 0 };
    return out;
  }, {} as Record<Threshold, { population: number; conversions: number }>);

  ladder.forEach((rung) => {
    const segment = tierOf(rung.score);
    totals[segment].population += rung.population;
    totals[segment].conversions += rung.conversions;
  });

  return TIER_ORDER.map((segment, index) => {
    const tier = totals[segment];
    return {
      segment,
      label: SEGMENT_LABEL[segment],
      color: segmentColor(segment),
      textColor: segmentTextColor(segment),
      threshold: index < cuts.length ? cuts[index] : null,
      population: tier.population,
      conversions: tier.conversions,
      share: total > 0 ? tier.population / total : 0,
      conversionShare: converted > 0 ? tier.conversions / converted : 0,
      rate:
        tier.population > 0 ? (tier.conversions / tier.population) * 100 : 0,
    };
  });
}

/**
 * The values a typed threshold may take without meeting a neighbour.
 *
 * The volume handles cannot cross or stack (`nearestShare`), and a typed
 * threshold is held to the same: Very Good above Good above Medium, at least one
 * step apart, so a keystroke cannot collapse a tier to nothing. Bounds are
 * inclusive, the shape an input's `min` and `max` take.
 */
export function thresholdRange(
  segment: Exclude<Threshold, 'low'>,
  thresholds: EnsembleThreshold,
  step: number
): { min?: number; max?: number } {
  const position = BOUNDED_TIERS.indexOf(segment);
  const above = BOUNDED_TIERS[position - 1];
  const below = BOUNDED_TIERS[position + 1];
  const range: { min?: number; max?: number } = {};
  if (below && Number.isFinite(Number(thresholds?.[below]))) {
    range.min = Number(thresholds[below]) + step;
  }
  if (above && Number.isFinite(Number(thresholds?.[above]))) {
    range.max = Number(thresholds[above]) - step;
  }
  return range;
}

/**
 * Why a typed threshold is refused, in its neighbours' own terms: the page holds
 * tiers apart silently otherwise, and a refused field with no reason reads as
 * broken. Empty when nothing bounds it.
 */
export function rangeHint(
  segment: Exclude<Threshold, 'low'>,
  thresholds: EnsembleThreshold
): string {
  const position = BOUNDED_TIERS.indexOf(segment);
  const known = (tier: Threshold | undefined) =>
    tier !== undefined && Number.isFinite(Number(thresholds?.[tier]));
  const named = (tier: Threshold) =>
    `${SEGMENT_LABEL[tier]} (${thresholds[tier]})`;
  const above = BOUNDED_TIERS[position - 1];
  const below = BOUNDED_TIERS[position + 1];
  if (known(above) && known(below)) {
    return `Keep between ${named(below)} and ${named(above)}`;
  }
  if (known(above)) return `Keep below ${named(above)}`;
  if (known(below)) return `Keep above ${named(below)}`;
  return '';
}

/**
 * The nearest share a handle may actually take, kept strictly inside its
 * neighbours so dragging can never swap two boundaries or stack them.
 *
 * Null when there is no rung in the gap, in which case the handle does not move:
 * the alternative is snapping it somewhere it was not dragged to.
 */
export function nearestShare(
  target: number,
  ladder: LadderRung[],
  lowerExclusive: number,
  upperExclusive: number
): number | null {
  let best: number | null = null;
  let distance = Infinity;
  ladder.forEach((rung) => {
    if (
      rung.share <= lowerExclusive + 1e-9 ||
      rung.share >= upperExclusive - 1e-9
    ) {
      return;
    }
    const gap = Math.abs(rung.share - target);
    if (gap < distance) {
      distance = gap;
      best = rung.share;
    }
  });
  return best;
}

/**
 * Percentages that sum to exactly 100, by largest remainder.
 *
 * Rounding each share on its own is what produces a stacked bar labelled
 * 12 + 36 + 20 + 31, which a reader is entitled to treat as a bug in the numbers
 * rather than in their presentation.
 */
export function shareLabels(values: number[]): number[] {
  const total = values.reduce((sum, value) => sum + value, 0);
  // Nothing to apportion. Falling back to a denominator of 1 here, as the
  // prototype did, hands out the 100 spare points one per band and labels an
  // empty bar 1% four times.
  if (total <= 0) return values.map(() => 0);

  const exact = values.map((value) => (100 * value) / total);
  const floors = exact.map(Math.floor);
  let left = 100 - floors.reduce((sum, value) => sum + value, 0);
  const byRemainder = exact
    .map((value, index) => [value - floors[index], index] as [number, number])
    .sort((first, second) => second[0] - first[0]);

  const out = floors.slice();
  for (
    let rank = 0;
    rank < byRemainder.length && left > 0;
    rank += 1, left -= 1
  ) {
    out[byRemainder[rank][1]] += 1;
  }
  return out;
}
